import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import { useAuth } from './AuthContext';
import './Shifts.css';

const Shifts = () => {
  const { hasRole } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStartModal, setShowStartModal] = useState(false);
  const [filterObjectId, setFilterObjectId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('active'); // 'active' | 'all' | 'sms' | 'auto' | 'dispatcher'
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const perPage = 20;
  const [startForm, setStartForm] = useState({
    object_id: '',
    employee_name: '',
    notes: '',
  });

  const objectMap = useMemo(() => {
    const map = {};
    for (const obj of objects) {
      map[obj.id] = obj;
    }
    return map;
  }, [objects]);

  useEffect(() => {
    fetchData();
    fetchObjects();
  }, [page, filterObjectId, filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { per_page: perPage, page };
      if (filterObjectId) params.object_id = filterObjectId;
      if (filterStatus === 'active') {
        params.active_only = true;
      } else if (filterStatus === 'sms') {
        params.close_type = 'sms';
      } else if (filterStatus === 'auto') {
        params.close_type = 'auto';
      } else if (filterStatus === 'dispatcher') {
        params.close_type = 'dispatcher';
      }
      
      const response = await api.get('/shifts', { params });
      setShifts(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching shifts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchObjects = async () => {
    try {
      const response = await api.get('/objects?per_page=500');
      setObjects(response.data.items);
    } catch (error) {
      console.error('Error fetching objects:', error);
    }
  };

  const handleStartShift = async (e) => {
    e.preventDefault();
    try {
      await api.post('/shifts/start', startForm);
      setShowStartModal(false);
      setStartForm({ object_id: '', employee_name: '', notes: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка начала смены');
    }
  };

  const handleEndShift = async (shiftId) => {
    if (!window.confirm('Вы действительно хотите завершить эту смену?')) {
      return;
    }
    try {
      await api.post(`/shifts/${shiftId}/end`, {});
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail;
      const errorText =
        typeof msg === 'string'
          ? msg
          : Array.isArray(msg)
          ? msg.map((m) => m.msg || m.message || JSON.stringify(m)).join(', ')
          : 'Ошибка завершения смены';
      alert(errorText);
    }
  };

  const handleFilter = () => {
    setPage(1);
    fetchData();
  };

  const resetFilter = () => {
    setFilterObjectId(null);
    setFilterStatus('active');
    setPage(1);
    fetchData();
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="shifts-page">
      <div className="page-header">
        <h2>Учёт смен</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn-secondary"
            onClick={async () => {
              try {
                const response = await api.get('/shifts/export/rep-excel', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `rep_${new Date().toISOString().slice(0, 10)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
              } catch (err) {
                alert('Ошибка скачивания отчета rep.xlsx');
              }
            }}
            title="Скачать суточный отчет в формате rep.xlsx по доступным филиалам"
          >
            📊 Скачать отчет (Excel)
          </button>
          <button className="btn-primary" onClick={() => setShowStartModal(true)}>
            + Начать смену
          </button>
        </div>
      </div>

      <div className="filters">
        <div className="filter-row">
          <select
            value={filterObjectId || ''}
            onChange={(e) => setFilterObjectId(e.target.value || null)}
          >
            <option value="">Все объекты</option>
            {objects.map((obj) => (
              <option key={obj.id} value={obj.id}>
                {obj.code ? `[${obj.code}] ` : ''}{obj.name}
              </option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value);
              setPage(1);
            }}
            title="Фильтр по статусу и способу закрытия смены"
            style={{ fontWeight: 500 }}
          >
            <option value="active">🟢 Только активные смены</option>
            <option value="all">📋 Все смены (история)</option>
            <option value="sms">📱 Закрытые по SMS</option>
            <option value="auto">⏰ Закрытые автоматически (21:00)</option>
            <option value="dispatcher">👤 Закрытые диспетчером</option>
          </select>

          <button className="btn-secondary" onClick={handleFilter}>
            Фильтр
          </button>
          <button className="btn-secondary" onClick={resetFilter}>
            Сброс
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Загрузка...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Код объекта</th>
                  <th>Объект</th>
                  <th>Филиал</th>
                  <th>Сотрудник</th>
                  <th>Начало</th>
                  <th>Окончание</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {shifts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      Нет смен
                    </td>
                  </tr>
                ) : (
                  shifts.map((shift) => {
                    const obj = objectMap[shift.object_id];
                    return (
                      <tr key={shift.id}>
                        <td className="post-code-cell">
                          <span className="post-code-badge">
                            {obj?.code || '-'}
                          </span>
                        </td>
                        <td>
                          <strong>{obj?.name || `Объект #${shift.object_id}`}</strong>
                          {obj?.address && (
                            <div className="object-address-sub">{obj.address}</div>
                          )}
                        </td>
                        <td>
                          {obj?.branch_name ? (
                            <span className="branch-tag">
                              {obj.branch_name}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{shift.employee_name}</td>
                        <td>{new Date(shift.started_at).toLocaleString('ru-RU')}</td>
                        <td>
                          {shift.ended_at
                            ? new Date(shift.ended_at).toLocaleString('ru-RU')
                            : '-'}
                        </td>
                        <td>
                          {shift.is_active ? (
                            <span className="status active">🟢 Активна</span>
                          ) : (
                            <div className="shift-close-summary" style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                              <span className={`close-type-badge close-type-${shift.close_type || 'default'}`}>
                                {shift.close_type === 'sms' && '📱 По SMS'}
                                {shift.close_type === 'auto' && '⏰ Авто (21:00)'}
                                {shift.close_type === 'dispatcher' && '👤 Диспетчером'}
                                {!shift.close_type && 'Завершена'}
                              </span>
                              {shift.close_type === 'sms' && (
                                <div style={{ fontSize: '0.78rem', color: '#1e293b' }}>
                                  Закрыл: <strong>{shift.ended_by_name || shift.employee_name}</strong>
                                </div>
                              )}
                              {shift.close_type === 'dispatcher' && shift.ended_by_name && (
                                <div style={{ fontSize: '0.78rem', color: '#1e293b' }}>
                                  Диспетчер: <strong>{shift.ended_by_name}</strong>
                                </div>
                              )}
                              {shift.notes && (
                                <div
                                  style={{
                                    fontSize: '0.74rem',
                                    color: '#64748b',
                                    maxWidth: '220px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={shift.notes}
                                >
                                  📝 {shift.notes}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          {shift.is_active && (
                            <button
                              type="button"
                              className="btn-end"
                              onClick={() => handleEndShift(shift.id)}
                              title="Завершить смену"
                            >
                              Завершить
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="btn-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Назад
              </button>
              <span className="page-info">
                Страница {page} из {totalPages} ({total} смен)
              </span>
              <button
                className="btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Вперёд
              </button>
            </div>
          )}
        </>
      )}

      {showStartModal && (
        <div className="modal-overlay" onClick={() => setShowStartModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Начать смену</h3>
            <form onSubmit={handleStartShift}>
              <div className="form-group">
                <label>Объект *</label>
                <select
                  value={startForm.object_id}
                  onChange={(e) =>
                    setStartForm({ ...startForm, object_id: e.target.value })
                  }
                  required
                >
                  <option value="">Выберите объект</option>
                  {objects.filter((obj) => obj.is_active).map((obj) => (
                    <option key={obj.id} value={obj.id}>
                      {obj.code ? `[${obj.code}] ` : ''}{obj.name}{obj.address ? ` - ${obj.address}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>ФИО сотрудника *</label>
                <input
                  type="text"
                  value={startForm.employee_name}
                  onChange={(e) =>
                    setStartForm({ ...startForm, employee_name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Примечания</label>
                <textarea
                  value={startForm.notes}
                  onChange={(e) =>
                    setStartForm({ ...startForm, notes: e.target.value })
                  }
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowStartModal(false)}
                >
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  Начать смену
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shifts;
